import { EventEmitter, h, Component, Prop, Watch, Element, Host, Event } from '@stencil/core';

import { parseProp } from '../../../services/utils';

const defaultObserverOptions: IntersectionObserverInit = { rootMargin: '25% 25%' };

@Component({
  tag: 'pk-lazy',
  styleUrl: './scss/pk-lazy.scss',
})
export class PkLazy {
  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkLazyElement;

  // Own Properties
  // ====================
  private observer: IntersectionObserver;

  // Public Property API
  // ========================

  /** Component name to load */
  @Prop() lazyComponent!: string;

  /** Interserction observer options */
  @Prop() lazyComponentOptions?: IntersectionObserverInit | string;

  // Prop lifecycle events
  // =======================

  @Watch('lazyComponent')
  @Watch('lazyComponentOptions')
  handleComponentPropChange() {
    this.initObserver();
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler to be called when the real component is loaded */
  @Event() pkLazyLoaded: EventEmitter<HTMLElement>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  connectedCallback() {
    this.initObserver();
  }

  disconnectedCallback() {
    this.removeObserver();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private initObserver() {
    this.removeObserver();

    const observerOptions = parseProp<IntersectionObserverInit>(this.lazyComponentOptions);

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        this.observer.unobserve(entry.target);
        this.loadComponent();
      },
      {
        ...defaultObserverOptions,
        ...observerOptions,
      },
    );

    this.observer.observe(this.el);
  }

  private removeObserver() {
    this.observer?.disconnect();
    this.observer = undefined;
  }

  private loadComponent = () => {
    const component = this.lazyComponent.startsWith('pk-') ? this.lazyComponent : `pk-${this.lazyComponent}`;

    // create a new component element
    const el = document.createElement(component);

    // copy all attributes from the host element to the new component element
    Array.from(this.el.attributes)
      .filter((attribute) => !attribute.name.startsWith('lazy-component'))
      .forEach((attribute) => el.setAttribute(attribute.name, attribute.value));

    // set innerHTML of the new component element
    el.innerHTML = this.el.innerHTML;

    // replace the host element with the new component element
    this.el.replaceWith(el);

    this.pkLazyLoaded.emit(el);
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return <Host />;
  }
}
